
import { computed, defineComponent, nextTick, onMounted, reactive, watch } from 'vue'

import StateMessage from './StateMessage.vue'
import ChatMessage from './ChatMessage.vue'
import FilesInput from './FilesInput.vue'

import { oktell } from '@/core/realisations/oktell.realisation'

import { OktellNewMessageEvent, OktellWhatsappMappedMessage, OktellWhatsappMessage, OktellWhatsappMessageData } from '@/core/types/oktell.types'

import useNotifications from '@/common/composable/useNotifications'
import usePusher from '@/modules/OktellModule/composable/usePusher'
import { useMaska } from '@/common/composable/useMaska'

const LICENSE_ID = 43311
const MESSENGER_TYPE = 'caWhatsApp'
const SUBSCRIBE_URL = `private-v1.licenses.${LICENSE_ID}.messengers.${MESSENGER_TYPE}`

export default defineComponent({
    components: {
        StateMessage,
        ChatMessage,
        FilesInput,
    },
    props: {
        disabled: {
            type: Boolean,
            default: false,
        },
        phone: {
            type: String,
            default: null,
        },
        email: {
            type: String,
            default: null
        }
    },
    setup(props) {
        let channel = null as any | null

        const notifications = useNotifications()
        const { unmaskNumbers } = useMaska()

        const chatHistory = reactive({
            is_loading: false,
            is_failed: false,
        })

        const chatStateMessage = reactive({
            is_loading: false,
            is_failed: false,
            message: null as string | null,
        })

        const chatWindow = reactive({
            is_loading: false,
            is_failed: false,
            messages: [] as OktellWhatsappMappedMessage[],
        })

        const chatControl = reactive({
            is_loading: false,
            is_failed: false,
            value: null as string | null,
            files: [] as File[],
        })

        const updateChatStateMessage = ({
            is_loading,
            is_failed,
            message,
        }: {
            is_loading: boolean
            is_failed: boolean
            message: string
        }) => {
            chatStateMessage.is_loading = is_loading
            chatStateMessage.is_failed = is_failed
            chatStateMessage.message = message
        }

        const closeChatStateMessage = () => {
            chatStateMessage.message = null
            chatStateMessage.is_loading = false
            chatStateMessage.is_failed = false
        }

        const getOnlyNumbers = (phone: string): string => {
            const replacedPhone = phone.replace(/\D/g, '')
            return replacedPhone.slice(1)
        }

        const filterCustomerMessagesNewEvent = (message: OktellWhatsappMessageData) => {
            if (message.fromUser) {
                const msgPhone = getOnlyNumbers(message.fromUser.phone)
                const propsPhone = getOnlyNumbers(props.phone)

                if (msgPhone === propsPhone) {
                    return true
                }

                const chatPhone = getOnlyNumbers(message.chat.phone)
                return message.fromMe && chatPhone === propsPhone
            }
            
            return false
        }

        const filterCustomerMessages = (message: OktellWhatsappMessage) => {
            const msgPhone = getOnlyNumbers(message.phone)
            const propsPhone = getOnlyNumbers(props.phone)

            if (msgPhone === propsPhone) {
                return true
            }

            if (message.data) {
                const chatPhone = getOnlyNumbers(message.data.chat.phone)
                return message.data.fromMe && chatPhone === propsPhone
            }

            return false
        }

        const onNewMessageEvent = (event: OktellNewMessageEvent) => {
            const filteredMessages = event.payload.data.filter(filterCustomerMessagesNewEvent).map((message) => {
                return {
                    data: message,
                    message: null,
                    phone: null
                }
            })

            chatWindow.messages = chatWindow.messages.concat(filteredMessages)
            scrollChatWindow()
        }

        const scrollChatWindow = () => {
            const element = document.getElementById('chat-window')
            if (!element) return

            nextTick(() => {
                element.scrollTop = element.scrollHeight
            })
        }

        const loadChatHistory = async () => {
            try {
                updateChatStateMessage({
                    is_loading: true,
                    is_failed: false,
                    message: 'Загрузка истории сообщений...',
                })

                chatHistory.is_loading = true

                const response = await oktell.getWhatsappHistory({
                    phone: props.phone,
                    sortBy: 'asc',
                    page: 1,
                    limit: 9999,
                })

                chatWindow.messages = response.data.filter(filterCustomerMessages).map((message) => {
                    return {
                        data: message.data,
                        message: message.message,
                        phone: message.phone
                    }
                })

                scrollChatWindow()
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При загрузке истории чата произошла ошибка ',
                })

                updateChatStateMessage({
                    is_loading: false,
                    is_failed: true,
                    message: 'Ошибка при загрузке истории сообщений',
                })

                console.error(error)
                chatHistory.is_failed = true
            } finally {
                chatHistory.is_loading = false
                setTimeout(closeChatStateMessage, 1000)
            }
        }

        const onPusherConnected = (instance: any) => {
            instance.unsubscribe(SUBSCRIBE_URL)

            updateChatStateMessage({
                is_loading: false,
                is_failed: false,
                message: 'Соединение установлено',
            })

            channel = instance.subscribe(SUBSCRIBE_URL)
            channel.bind('message', onNewMessageEvent)

            setTimeout(closeChatStateMessage, 1000)

            loadChatHistory()
        }

        const onPusherConnectFailed = () => {
            updateChatStateMessage({
                is_loading: false,
                is_failed: true,
                message: 'Ошибка соединения',
            })
        }

        const pusher = usePusher({
            successCallback: onPusherConnected,
            failureCallback: onPusherConnectFailed,
        })

        onMounted(() => {
            updateChatStateMessage({
                is_loading: true,
                is_failed: false,
                message: 'Установка соединения...',
            })

            pusher.installScript()
        })

        const onSubmitMessage = async () => {
            if (isComponentDisabled.value) return

            updateChatStateMessage({
                is_loading: true,
                is_failed: false,
                message: 'Отправка сообщения...',
            })

            try {
                chatControl.is_loading = true

                const form_data = new FormData()

                form_data.append('message', chatControl.value as string)
                form_data.append('phone', props.phone)
                form_data.append('senderName', props.email)

                chatControl.files.forEach((file) => {
                    form_data.append('files[]', file)
                })

                // await oktell.sendWhatsappMessage({
                //     message: chatControl.value as string,
                //     phone: props.phone,
                // })
                await oktell.sendWhatsappMessage(form_data)

                chatControl.is_failed = false

                chatControl.value = null
                chatControl.files = []
            } catch (error) {
                console.error(error)

                notifications.notifyError({
                    error,
                    title: 'При отправке сообщения произошла ошибка',
                })

                updateChatStateMessage({
                    is_loading: false,
                    is_failed: true,
                    message: 'Ошибка отправки сообщения',
                })
            } finally {
                chatControl.is_loading = false
                setTimeout(closeChatStateMessage, 1000)
            }
        }

        const removeFile = (file_index: number) => {
            if (isComponentDisabled.value) return
            chatControl.files.splice(file_index, 1)
        }

        const isComponentDisabled = computed(() => {
            return (
                chatStateMessage.is_loading ||
                chatWindow.is_loading ||
                chatControl.is_loading ||
                chatHistory.is_loading ||
                props.disabled
            )
        })

        return {
            chatStateMessage,
            chatWindow,
            chatControl,
            //
            isComponentDisabled,
            onSubmitMessage,
            removeFile,
        }
    },
})
